import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Calendar,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  HomeOutlined,
  MoreOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { httpsCallable } from "firebase/functions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db, functions } from "../../firebase";
import PageHeader from "../../components/pageHeader/PageHeader";
import Loader from "../../components/loader/loader";
import { GreenNotify, RedNotify } from "../../helper/helper";
import { statusColor, statusLabel, normalizeStatus } from "../../helper/statusMeta";
import {
  effectiveDeadline,
  isOpenInspection,
  isOverdue,
  normalizeVisits,
  nextOwnerVisit,
  nextInspectionReminder,
} from "../../helper/scheduling";
import { isAssignableInspector, getUserLabel } from "../../helper/userDisplay";
import { buildInspectionDoc } from "../../helper/inspections";
import AssignInspectors from "../../components/AssignInspectors/AssignInspectors";
import DiscussionDrawer from "../../components/DiscussionDrawer";
import { useSelector } from "react-redux";
import { subjectThreadId } from "../../helper/discussions";
import "./inspectionsScheduler.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const KEY = "YYYY-MM-DD";
const DEFAULT_TZ = "America/Denver";

// Treat a property's denormalized currentInspection summary like an inspection
// for the scheduling-math helpers (effectiveDeadline / isOverdue / isOpen).
const currentOf = (property) => property.currentInspection || null;

// Format an effective-deadline moment in the PROPERTY's timezone, so the date
// label matches when the deadline actually falls there (not the viewer's zone).
const fmtInTz = (eff, tz, fmt) =>
  eff ? dayjs(eff.valueOf()).tz(tz || DEFAULT_TZ).format(fmt) : "—";
const dayKeyInTz = (eff, tz) =>
  dayjs(eff.valueOf()).tz(tz || DEFAULT_TZ).format(KEY);

const intervalWeeksOf = (property) =>
  property?.inspectionIntervalWeeks > 0 ? property.inspectionIntervalWeeks : 1;

const frequencyLabel = (w) =>
  w === 1 ? "Weekly" : w === 4 ? "Monthly" : `Every ${w} wks`;

// Stable per-property color so each inspection's window band + connector line is
// distinguishable on the calendar. Hash the id → a fixed palette slot (so the
// same property always gets the same color regardless of load order).
const BAND_COLORS = [
  { line: "#c2603d", fill: "#f7e4d9" }, // clay
  { line: "#3d7ea6", fill: "#dcecf5" }, // blue
  { line: "#5a9367", fill: "#e1efe4" }, // green
  { line: "#9a6fb0", fill: "#ece0f3" }, // purple
  { line: "#c79a2f", fill: "#f6ecd0" }, // gold
  { line: "#3d9a91", fill: "#d9efed" }, // teal
  { line: "#a6533d", fill: "#f2e1db" }, // brick
  { line: "#7d8a3a", fill: "#eef0d6" }, // olive
];
const colorForProperty = (id) => {
  const s = String(id || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return BAND_COLORS[h % BAND_COLORS.length];
};

// True if the property ALREADY has an inspection covering its current cadence
// window — so "Schedule now" would create a duplicate for this period. Prefer
// the current inspection's own window (windowStart + interval); fall back to the
// cadence clock (nextInspectionDue) for inspections that never stamped a window.
const alreadyScheduledThisWindow = (property) => {
  const tz = property.timezone || DEFAULT_TZ;
  const today = dayjs().tz(tz).format(KEY);
  const ws = property.currentInspection?.windowStart;
  if (ws) {
    return today < dayjs(ws).add(intervalWeeksOf(property), "week").format(KEY);
  }
  if (property.nextInspectionDue) return today < property.nextInspectionDue;
  return false;
};

const InspectionsScheduler = () => {
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("List");
  // ?filter=Missed etc. preselects a chip (dashboard deep links); bad values
  // fall back to All. Same pattern as /tasks?questions=1.
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get("filter");
  const [filter, setFilter] = useState(
    ["Unassigned", "Overdue", "Needs attention", "Missed", "Cancelled", "Owner arrival"].includes(urlFilter)
      ? urlFilter
      : "All",
  );
  const [selectedDay, setSelectedDay] = useState(null); // "YYYY-MM-DD" | null
  const [visitsModalProp, setVisitsModalProp] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // row whose inspection to cancel
  const [discussionProp, setDiscussionProp] = useState(null); // property row whose inspection thread is open
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false); // separate from `busy` so cancel doesn't share spinners with scheduleNow/owner-visits
  const [busy, setBusy] = useState(false);
  const [remindingId, setRemindingId] = useState(null); // propertyId whose reminder push is in flight
  // Live set of my unread discussion-thread ids → dot on the Discussion action.
  const unreadThreadIds = useSelector(
    (s) => s.userDataSlice?.unreadThreadIds || [],
  );
  const unreadSet = useMemo(() => new Set(unreadThreadIds), [unreadThreadIds]);
  const navigate = useNavigate();

  // ---- realtime data: properties + users ONLY ----------------------------
  // The scheduler reads each property's denormalized `currentInspection`
  // summary (maintained by the syncPropertyStatus Cloud Function), so it never
  // has to stream the whole (unbounded) inspections collection.
  useEffect(() => {
    const unsubP = onSnapshot(
      collection(db, "properties"),
      (snap) => {
        setProperties(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
        setLoading(false);
      },
      (e) => {
        console.error("scheduler: properties stream", e);
        setLoading(false);
      },
    );
    const unsubU = onSnapshot(
      collection(db, "users"),
      (snap) => setUsers(snap.docs.map((d) => ({ _id: d.id, ...d.data() }))),
      (e) => console.error("scheduler: users stream", e),
    );
    return () => {
      unsubP();
      unsubU();
    };
  }, []);

  const userLabel = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      m[u._id] = getUserLabel(u);
    });
    return m;
  }, [users]);
  const inspectors = useMemo(() => users.filter(isAssignableInspector), [users]);

  const inspectorNames = (property) => {
    const ids = Array.isArray(property.assignedInspectors)
      ? property.assignedInspectors
      : [];
    return ids.map((u) => userLabel[u] || "Unknown");
  };

  const saveInspectors = async (propertyId, assignedInspectors) => {
    await updateDoc(doc(db, "properties", propertyId), { assignedInspectors });
  };

  // Fire an on-demand push reminder to the assigned inspector(s) via the
  // remindInspectors callable. The callable stamps lastReminderAt on the
  // property, so the "Reminded …" label updates itself through the live stream.
  const sendReminder = async (r) => {
    if (!r?._id || remindingId) return;
    try {
      setRemindingId(r._id);
      const remind = httpsCallable(functions, "remindInspectors");
      const res = await remind({ propertyId: r._id });
      const sent = res?.data?.sent || 0;
      GreenNotify(
        sent > 0
          ? `Reminder sent to ${sent} inspector${sent === 1 ? "" : "s"}.`
          : "No reminder could be delivered (no devices registered).",
      );
    } catch (e) {
      RedNotify(e?.message || "Couldn't send the reminder.");
    } finally {
      setRemindingId(null);
    }
  };

  // ---- derived rows -------------------------------------------------------
  const rows = useMemo(
    () =>
      properties.map((p) => {
        const current = currentOf(p);
        const open = current ? isOpenInspection(current) : false;
        const eff = open ? effectiveDeadline(current) : null;
        const assigned = Array.isArray(p.assignedInspectors)
          ? p.assignedInspectors
          : [];
        const declined = Array.isArray(current?.declinedBy)
          ? current.declinedBy
          : [];
        // "Needs attention": open, has assignees, and ALL of them declined —
        // nobody is going to do it unless an admin reassigns.
        const allDeclined =
          open && assigned.length > 0 && assigned.every((u) => declined.includes(u));
        return {
          ...p,
          _current: current,
          _open: open,
          _eff: eff,
          _overdue: open ? isOverdue(current) : false,
          _unassigned: assigned.length === 0,
          _thorough: !!current?.thorough,
          _declined: open ? declined.filter((u) => assigned.includes(u)) : [],
          _allDeclined: allDeclined,
          _missed: p.lastMissed || null,
          _cancelled: p.lastCancelled || null,
        };
      }),
    [properties],
  );

  const filteredRows = useMemo(() => {
    const r =
      filter === "Unassigned"
        ? rows.filter((x) => x._unassigned)
        : filter === "Overdue"
          ? rows.filter((x) => x._overdue)
          : filter === "Needs attention"
            ? rows.filter((x) => x._allDeclined)
            : filter === "Missed"
              ? rows.filter((x) => x._missed)
              : filter === "Cancelled"
                ? rows.filter((x) => x._cancelled)
                : filter === "Owner arrival"
                  ? rows.filter((x) => x._thorough)
                  : rows;
    // Soonest deadline first; properties with no open inspection sink to the end.
    return [...r].sort(
      (a, b) =>
        (a._eff?.valueOf() || Infinity) - (b._eff?.valueOf() || Infinity),
    );
  }, [rows, filter]);

  // Owner-visit days, precomputed once (vs scanning every property per cell).
  const ownersByDay = useMemo(() => {
    const m = {};
    properties.forEach((p) => {
      normalizeVisits(p).forEach((v) => {
        const s = moment(v.startDate);
        const e = v.endDate && moment(v.endDate).isValid() ? moment(v.endDate) : s;
        if (!s.isValid()) return;
        const cur = s.clone().startOf("day");
        const end = e.clone().startOf("day");
        let guard = 0;
        while (cur.isSameOrBefore(end, "day") && guard < 400) {
          const k = cur.format(KEY);
          (m[k] = m[k] || []).push(p);
          cur.add(1, "day");
          guard += 1;
        }
      });
    });
    return m;
  }, [properties]);

  // Open inspections keyed by their effective-deadline day (for the calendar
  // due markers + the day drawer's "due this day" list).
  const dueByDay = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!r._open || !r._eff) return;
      const k = dayKeyInTz(r._eff, r.timezone);
      (m[k] = m[k] || []).push(r);
    });
    return m;
  }, [rows]);

  // Assign each property a distinct band color by index (sorted for stability),
  // so visible properties never collide on the same color (cycles after 8).
  const colorByProperty = useMemo(() => {
    const ids = [...new Set(rows.map((r) => r._id))].sort();
    const m = {};
    ids.forEach((id, i) => {
      m[id] = BAND_COLORS[i % BAND_COLORS.length];
    });
    return m;
  }, [rows]);

  // Each open inspection's full allowed PERIOD (windowStart → due day), keyed by
  // day, so the calendar can paint a band across the window: an "opens" marker on
  // the start day, plain tint on the days between, and the status/due flag on the
  // deadline day.
  const windowByDay = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!r._open || !r._eff) return;
      const dueKey = dayKeyInTz(r._eff, r.timezone);
      const startKey = r._current?.windowStart || dueKey;
      let cur = moment(startKey, "YYYY-MM-DD", true);
      const end = moment(dueKey, "YYYY-MM-DD", true);
      if (!cur.isValid() || !end.isValid() || cur.isAfter(end)) {
        // No usable window — fall back to a single due-day marker.
        (m[dueKey] = m[dueKey] || []).push({ r, isStart: true, isDue: true });
        return;
      }
      for (let guard = 0; cur.isSameOrBefore(end, "day") && guard < 60; guard++) {
        const key = cur.format("YYYY-MM-DD");
        (m[key] = m[key] || []).push({
          r,
          isStart: key === startKey,
          isDue: key === dueKey,
        });
        cur = cur.add(1, "day");
      }
    });
    return m;
  }, [rows]);

  // ---- summary ------------------------------------------------------------
  const openCount = rows.filter((r) => r._open).length;
  const unassignedCount = rows.filter((r) => r._unassigned).length;
  const overdueCount = rows.filter((r) => r._overdue).length;
  const ownerCount = rows.filter((r) => r._thorough).length;
  const attentionCount = rows.filter((r) => r._allDeclined).length;
  const missedCount = rows.filter((r) => r._missed).length;
  const cancelledCount = rows.filter((r) => r._cancelled).length;

  // ---- actions ------------------------------------------------------------
  // Force-create an inspection now (ad-hoc) for a property without an open one.
  // Inspectors are inherited from the property; the weekly job handles cadence.
  const scheduleNow = async (property) => {
    setBusy(true);
    try {
      await addDoc(
        collection(db, "inspections"),
        buildInspectionDoc({
          property,
          assignedInspectors: Array.isArray(property.assignedInspectors)
            ? property.assignedInspectors
            : [],
        }),
      );
      GreenNotify("Inspection scheduled.");
    } catch (e) {
      console.error(e);
      RedNotify("Could not schedule the inspection.");
    } finally {
      setBusy(false);
    }
  };

  // Gate Schedule-now: warn if the property has already been inspected for its
  // current frequency cycle (creating another now would duplicate this window).
  const confirmScheduleNow = (property) => {
    if (!alreadyScheduledThisWindow(property)) return scheduleNow(property);
    const freq = frequencyLabel(intervalWeeksOf(property)).toLowerCase();
    const nextDue = property.nextInspectionDue
      ? ` Its next inspection is already due ${moment(
          property.nextInspectionDue,
          "YYYY-MM-DD",
        ).format("ddd, MMM D")}.`
      : "";
    Modal.confirm({
      title: "Already inspected this cycle",
      content: `${property.propertyName || "This property"} has already been inspected for its current ${freq} cycle.${nextDue} Schedule another inspection now anyway?`,
      okText: "Schedule anyway",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => scheduleNow(property),
    });
  };

  // Soft-cancel the property's CURRENT inspection (this window only). Keeps a
  // terminal "cancelled" status on the doc (tracked alongside missed/completed),
  // and a Cloud Function pushes the assigned inspectors so they don't do it. The
  // weekly reset still creates the next window's inspection on cadence.
  const cancelInspection = async () => {
    if (cancelBusy) return; // guard against a double-click firing two writes
    const inspectionId = cancelTarget?._current?.id;
    if (!inspectionId) {
      RedNotify("No current inspection to cancel.");
      return;
    }
    setCancelBusy(true);
    try {
      await updateDoc(doc(db, "inspections", inspectionId), {
        status: "cancelled",
        cancelledAt: new Date().toISOString(),
        cancelledBy: auth.currentUser?.uid || null,
        cancelReason: cancelReason.trim() || null,
        updatedAt: new Date().toISOString(),
      });
      GreenNotify("Inspection cancelled — inspectors notified.");
      setCancelTarget(null);
      setCancelReason("");
    } catch (e) {
      console.error(e);
      RedNotify("Could not cancel the inspection.");
    } finally {
      setCancelBusy(false);
    }
  };

  const saveOwnerVisits = async (propertyId, ownerVisits) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, "properties", propertyId), { ownerVisits });
      GreenNotify("Owner visits updated.");
      setVisitsModalProp(null);
    } catch (e) {
      console.error(e);
      RedNotify("Could not update owner visits.");
    } finally {
      setBusy(false);
    }
  };

  // ---- table --------------------------------------------------------------
  const columns = [
    {
      title: "Property",
      key: "p",
      render: (_, r) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => navigate(`/properties/${r._id}`)}
        >
          {r.propertyName || "Untitled"}
        </Button>
      ),
    },
    {
      title: "Inspector(s)",
      key: "i",
      width: 280,
      render: (_, r) => (
        <AssignInspectors
          value={Array.isArray(r.assignedInspectors) ? r.assignedInspectors : []}
          options={inspectors}
          onSave={(uids) => saveInspectors(r._id, uids)}
        />
      ),
    },
    {
      title: "Frequency",
      key: "f",
      render: (_, r) => frequencyLabel(intervalWeeksOf(r)),
    },
    {
      title: "This inspection",
      key: "s",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r._current ? (
            <Tag color={statusColor(r._current.status)}>
              {statusLabel(r._current.status)}
            </Tag>
          ) : (
            <Tag>none yet</Tag>
          )}
          {(() => {
            const cur = r._current;
            if (!cur) return null;
            const fmt = (d) =>
              d ? fmtInTz(moment(d), r.timezone, "ddd, MMM D") : null;
            const st = normalizeStatus(cur.status);
            if (st === "completed") {
              const d = fmt(cur.completedAt);
              return d ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Completed {d}
                </Text>
              ) : null;
            }
            if (st === "in progress") {
              if (!cur.claimedBy) return null;
              const who =
                cur.claimedByName || userLabel[cur.claimedBy] || "an inspector";
              const d = fmt(cur.claimedAt);
              return (
                <Text style={{ fontSize: 12, color: "#3d7ea6" }}>
                  👤 Claimed by {who}
                  {d ? ` · ${d}` : ""}
                </Text>
              );
            }
            if (st === "pending") {
              // Fall back to windowStart for older inspections that predate the
              // createdAt being denormalized onto the summary.
              const d = fmt(cur.createdAt || cur.windowStart);
              return d ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Created {d}
                </Text>
              ) : null;
            }
            // cancelled / missed have their own dedicated lines below.
            return null;
          })()}
          {r._missed && (
            <Text type="danger" style={{ fontSize: 12 }}>
              ⚠ Missed
              {r._missed.windowStart
                ? ` week of ${moment(r._missed.windowStart).format("MMM D")}`
                : ""}
            </Text>
          )}
          {r._cancelled && (
            <Tooltip
              title={
                `Cancelled${
                  r._cancelled.by
                    ? ` by ${userLabel[r._cancelled.by] || "an admin"}`
                    : ""
                }` +
                (r._cancelled.reason ? ` — ${r._cancelled.reason}` : "")
              }
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                ✕ Cancelled
                {r._cancelled.windowStart
                  ? ` week of ${moment(r._cancelled.windowStart).format("MMM D")}`
                  : ""}
              </Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Due by",
      key: "d",
      render: (_, r) => {
        if (!r._open) return <Text type="secondary">—</Text>;
        // Show the last MANUAL reminder if one's gone out for this inspection,
        // otherwise the next automatic (inspectionDeadlineReminders) one.
        const reminded =
          r.lastReminderAt && r.lastReminderInspectionId === r._current?.id;
        const nextReminder = reminded ? null : nextInspectionReminder(r._current);
        return (
          <Space direction="vertical" size={0}>
            <span className={r._overdue ? "sched-overdue" : ""}>
              {r._eff ? fmtInTz(r._eff, r.timezone, "ddd, MMM D") : "—"}
              {r._overdue ? " (overdue)" : ""}
            </span>
            {reminded && (
              <span style={{ fontSize: 12, color: "#c2603d", fontWeight: 500 }}>
                🔔 last: {fmtInTz(moment(r.lastReminderAt), r.timezone, "MMM D, h:mm A")}
              </span>
            )}
            {nextReminder && (
              <span style={{ fontSize: 12, color: "#c2603d", fontWeight: 500 }}>
                🔔 next: {fmtInTz(nextReminder, r.timezone, "ddd, MMM D")}
              </span>
            )}
            {r._thorough && r._current?.ownerArrivalDate && (
              <Text type="warning" style={{ fontSize: 12 }}>
                ★ Owner arrives{" "}
                {moment(r._current.ownerArrivalDate).format("ddd MMM D")} —
                thorough
              </Text>
            )}
            {r._declined.length > 0 && (
              <Text type="danger" style={{ fontSize: 12 }}>
                ⚠ {r._declined.map((u) => userLabel[u] || "Unknown").join(", ")}{" "}
                can't do it{r._allDeclined ? " — needs reassign" : ""}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Next inspection",
      key: "next",
      render: (_, r) => {
        if (!r.nextInspectionDue) return <Text type="secondary">—</Text>;
        const m = moment(r.nextInspectionDue, "YYYY-MM-DD", true);
        if (!m.isValid()) return <Text type="secondary">—</Text>;
        // A stored next-due in the past means the cadence cron is behind (it
        // hasn't generated this window yet) — show "due now" rather than a
        // misleading past date.
        const today = dayjs().tz(r.timezone || DEFAULT_TZ).format(KEY);
        const isPast = r.nextInspectionDue < today;
        return (
          <span className={isPast ? "sched-overdue" : ""}>
            {m.format("ddd, MMM D")}
            {isPast ? " (due now)" : ""}
          </span>
        );
      },
    },
    {
      title: "",
      key: "x",
      width: 56,
      align: "right",
      render: (_, r) => {
        const hasUnread =
          !!r._current?.id &&
          unreadSet.has(subjectThreadId("inspection", r._current.id));
        const items = [
          {
            key: "owner",
            label: "Owner visits",
            onClick: () => setVisitsModalProp(r),
          },
        ];
        if (r._current?.id) {
          items.push({
            key: "discussion",
            label: (
              <Badge dot={hasUnread} color="#c2603d" offset={[6, 2]}>
                Discussion
              </Badge>
            ),
            onClick: () => setDiscussionProp(r),
          });
        }
        if (r._open && r._current?.id) {
          const noInspectors =
            !Array.isArray(r.assignedInspectors) ||
            r.assignedInspectors.length === 0;
          items.push({
            key: "remind",
            disabled: noInspectors || remindingId === r._id,
            label: noInspectors
              ? "Send reminder (no inspector)"
              : remindingId === r._id
                ? "Sending reminder…"
                : "Send reminder",
            onClick:
              noInspectors || remindingId === r._id
                ? undefined
                : () => sendReminder(r),
          });
          items.push({
            key: "cancel",
            danger: true,
            label: "Cancel inspection",
            onClick: () => setCancelTarget(r),
          });
        }
        if (!r._open) {
          const noChecklist =
            !Array.isArray(r.checklistTemplate) ||
            r.checklistTemplate.length === 0;
          items.push({
            key: "schedule",
            disabled: noChecklist,
            label: noChecklist
              ? "Schedule now (needs a checklist)"
              : "Schedule now",
            onClick: noChecklist ? undefined : () => confirmScheduleNow(r),
          });
        }
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Badge dot={hasUnread} color="#c2603d" offset={[-2, 4]}>
              <Button size="small" type="text" icon={<MoreOutlined />} />
            </Badge>
          </Dropdown>
        );
      },
    },
  ];

  // ---- calendar cell ------------------------------------------------------
  const renderCell = (current) => {
    const k = current.format(KEY);
    const wins = windowByDay[k] || [];
    const ownerProps = ownersByDay[k] || [];
    if (wins.length === 0 && ownerProps.length === 0) return null;
    const shown = wins.slice(0, 4);
    return (
      <ul className="sched-cell">
        {ownerProps.length > 0 && (
          <li className="sched-owner">
            <HomeOutlined /> Owner: {ownerProps.length}
          </li>
        )}
        {shown.map(({ r, isStart, isDue }) => {
          const isMid = !isStart && !isDue;
          const cls =
            "sched-band" +
            (isDue ? " sched-band-end" : "") +
            (isStart && !isDue ? " sched-band-start" : "") +
            (isMid ? " sched-band-mid" : "");
          const c = colorByProperty[r._id] || colorForProperty(r._id);
          const style = isMid
            ? {
                background: `linear-gradient(to bottom, transparent 7px, ${c.line} 7px, ${c.line} 10px, transparent 10px)`,
              }
            : {
                background: c.fill,
                ...(isStart ? { borderLeft: `3px solid ${c.line}` } : {}),
                ...(isDue ? { borderRight: `3px solid ${c.line}` } : {}),
              };
          const range = `${fmtInTz(
            moment(r._current?.windowStart || r._eff),
            r.timezone,
            "MMM D",
          )} – ${fmtInTz(r._eff, r.timezone, "MMM D")}`;
          return (
            <li
              key={r._id}
              className={cls}
              style={style}
              title={`${r.propertyName || "Inspection"}: allowed ${range}`}
            >
              {isDue ? (
                <span className={r._overdue ? "sched-overdue" : ""}>
                  <span style={{ color: c.line }}>⚑</span>{" "}
                  {r.propertyName || "Inspection"}
                  {r._thorough ? " ★" : ""}
                </span>
              ) : isStart ? (
                <span>
                  <span style={{ color: c.line }}>▸</span>{" "}
                  {r.propertyName || "Inspection"}{" "}
                  <span className="sched-open-tag">opens</span>
                </span>
              ) : (
                <span>&nbsp;</span>
              )}
            </li>
          );
        })}
        {wins.length > 4 && (
          <li className="sched-more">+{wins.length - 4} more</li>
        )}
      </ul>
    );
  };

  const dayOwners = selectedDay ? ownersByDay[selectedDay] || [] : [];
  const dayWindowItems = selectedDay ? windowByDay[selectedDay] || [] : [];
  const dayDueItems = dayWindowItems.filter((w) => w.isDue).map((w) => w.r);
  const dayOpenItems = dayWindowItems.filter((w) => !w.isDue).map((w) => w.r);

  // One inspection row in the day drawer — color swatch (matches the calendar
  // band), status, window range, inspectors, and quick actions.
  const renderDayInsp = (r) => {
    const c = colorByProperty[r._id] || colorForProperty(r._id);
    const range = `${fmtInTz(
      moment(r._current?.windowStart || r._eff),
      r.timezone,
      "MMM D",
    )} – ${fmtInTz(r._eff, r.timezone, "MMM D")}`;
    return (
      <div key={r._id} className="sched-drawer-item">
        <div style={{ flex: 1 }}>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: 3,
              background: c.line,
              marginRight: 6,
            }}
          />
          <Text strong>{r.propertyName || "Inspection"}</Text>{" "}
          <Tag color={statusColor(r._current?.status)}>
            {statusLabel(r._current?.status)}
          </Tag>
          {r._thorough && <Tag color="warning">★ thorough</Tag>}
          <div style={{ fontSize: 13, color: "#6b6258", marginTop: 2 }}>
            Window {range}
            {r._eff
              ? ` · due ${fmtInTz(r._eff, r.timezone, "ddd MMM D")}${
                  r._overdue ? " (overdue)" : ""
                }`
              : ""}
          </div>
          <div style={{ fontSize: 13, color: "#6b6258" }}>
            {inspectorNames(r).join(", ") || "Unassigned"}
          </div>
          {r._open && r._current?.claimedBy && (
            <div style={{ fontSize: 13, color: "#3d7ea6" }}>
              👤 Claimed by{" "}
              {r._current.claimedByName ||
                userLabel[r._current.claimedBy] ||
                "an inspector"}
            </div>
          )}
        </div>
        <Space direction="vertical" size={4}>
          <Button
            size="small"
            onClick={() => {
              setSelectedDay(null);
              navigate(`/properties/${r._id}`);
            }}
          >
            View
          </Button>
          {r._current?.id && (
            <Button
              size="small"
              onClick={() => {
                setSelectedDay(null);
                setDiscussionProp(r);
              }}
            >
              Discussion
            </Button>
          )}
        </Space>
      </div>
    );
  };

  return (
    <div className="admin-products-main-container">
      <Loader loading={loading} />
      <PageHeader
        eyebrow="Operations"
        title="Inspections Scheduler"
        subtitle="Every property and its current inspection — assign inspectors, set owner-visit windows, watch deadlines."
        actions={
          <Space wrap>
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { label: "List", value: "List", icon: <UnorderedListOutlined /> },
                { label: "Calendar", value: "Calendar", icon: <CalendarOutlined /> },
              ]}
            />
          </Space>
        }
      />

      <div className="sched-summary">
        <Tag color="processing">{openCount} open</Tag>
        <Tag
          color={unassignedCount ? "error" : "default"}
          style={{ cursor: "pointer" }}
          onClick={() => setFilter("Unassigned")}
        >
          {unassignedCount} unassigned
        </Tag>
        <Tag
          color={overdueCount ? "error" : "default"}
          style={{ cursor: "pointer" }}
          onClick={() => setFilter("Overdue")}
        >
          {overdueCount} overdue
        </Tag>
        <Tag
          color={attentionCount ? "error" : "default"}
          style={{ cursor: "pointer" }}
          onClick={() => setFilter("Needs attention")}
        >
          ⚠ {attentionCount} needs attention
        </Tag>
        <Tag
          color={missedCount ? "error" : "default"}
          style={{ cursor: "pointer" }}
          onClick={() => setFilter("Missed")}
        >
          {missedCount} missed
        </Tag>
        <Tag
          color="default"
          style={{ cursor: "pointer" }}
          onClick={() => setFilter("Cancelled")}
        >
          ✕ {cancelledCount} cancelled
        </Tag>
        <Tag
          color={ownerCount ? "warning" : "default"}
          style={{ cursor: "pointer" }}
          onClick={() => setFilter("Owner arrival")}
        >
          ★ {ownerCount} owner-arrival
        </Tag>
      </div>

      {view === "List" ? (
        <>
          <Segmented
            style={{ marginBottom: 12 }}
            value={filter}
            onChange={setFilter}
            options={["All", "Unassigned", "Overdue", "Needs attention", "Missed", "Cancelled", "Owner arrival"]}
          />
          <Table
            rowKey="_id"
            rowClassName={(_, index) =>
              index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row"
            }
            columns={columns}
            dataSource={filteredRows}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{ emptyText: <Empty description="No properties" /> }}
          />
        </>
      ) : (
        <div className="sched-calendar">
          <Calendar
            cellRender={(current, info) =>
              info.type === "date" ? renderCell(current) : info.originNode
            }
            onSelect={(value, selectInfo) => {
              if (!selectInfo || selectInfo.source === "date") {
                setSelectedDay(value.format(KEY));
              }
            }}
          />
        </div>
      )}

      {/* Day drawer-as-modal */}
      <Modal
        title={selectedDay ? moment(selectedDay).format("dddd, MMM D") : ""}
        open={!!selectedDay}
        onCancel={() => setSelectedDay(null)}
        footer={null}
      >
        {dayOwners.length > 0 && (
          <>
            <Title level={5}>
              <HomeOutlined /> Owner visiting
            </Title>
            {dayOwners.map((p) => (
              <div key={p._id}>
                <Text strong>{p.propertyName}</Text>
                <Button type="link" size="small" onClick={() => setVisitsModalProp(p)}>
                  manage
                </Button>
              </div>
            ))}
          </>
        )}
        {dayDueItems.length > 0 && (
          <>
            <Title level={5}>Due this day</Title>
            {dayDueItems.map(renderDayInsp)}
          </>
        )}

        {dayOpenItems.length > 0 && (
          <>
            <Title level={5} style={{ marginTop: dayDueItems.length ? 16 : 0 }}>
              In window — due later
            </Title>
            {dayOpenItems.map(renderDayInsp)}
          </>
        )}

        {dayDueItems.length === 0 && dayOpenItems.length === 0 && (
          <>
            <Title level={5}>Inspections</Title>
            <Empty description="No inspection windows active this day" />
          </>
        )}
      </Modal>

      {/* Cancel current inspection */}
      <Modal
        title="Cancel this inspection?"
        open={!!cancelTarget}
        onCancel={() => {
          setCancelTarget(null);
          setCancelReason("");
        }}
        okText="Cancel inspection"
        okButtonProps={{ danger: true, loading: cancelBusy }}
        cancelText="Keep it"
        onOk={cancelInspection}
        destroyOnClose
      >
        <p style={{ marginTop: 0, color: "#6b6258" }}>
          This cancels{" "}
          <strong>{cancelTarget?.propertyName || "this property"}</strong>'s
          current inspection. The assigned inspectors are notified so they don't
          do it. The next inspection is still created on schedule.
        </p>
        <Form layout="vertical">
          <Form.Item label="Reason (optional)" style={{ marginBottom: 0 }}>
            <Input.TextArea
              autoSize={{ minRows: 2 }}
              maxLength={200}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Property access unavailable this week"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Owner visits */}
      <OwnerVisitsModal
        open={!!visitsModalProp}
        property={visitsModalProp}
        properties={properties}
        busy={busy}
        onClose={() => setVisitsModalProp(null)}
        onSave={saveOwnerVisits}
      />

      {/* Inspection discussion thread */}
      <DiscussionDrawer
        open={!!discussionProp}
        onClose={() => setDiscussionProp(null)}
        kind="group"
        subjectType="inspection"
        subjectId={discussionProp?._current?.id}
        title={`Inspection — ${discussionProp?.propertyName || ""}`}
        propertyName={discussionProp?.propertyName}
        propertyId={discussionProp?._id || null}
        /* Seed empty: an inspection thread is read-gated on the inspection's own
           assignedInspectors, and the notify function fans out from that live
           set — seeding the (possibly wider) property team here would make people
           participants who can't actually open the thread. */
        participants={[]}
        authorName={auth.currentUser?.displayName || auth.currentUser?.email}
      />
    </div>
  );
};

// --- Owner-visit manager: pick a property, add/remove date-range visits ------
const OwnerVisitsModal = ({ open, property, properties, busy, onClose, onSave }) => {
  const [propertyId, setPropertyId] = useState(null);
  const [visits, setVisits] = useState([]);
  const [range, setRange] = useState(null);

  useEffect(() => {
    if (!open) return;
    const pid = property && property._id ? property._id : null;
    setPropertyId(pid);
    setVisits(normalizeVisits(property));
    setRange(null);
  }, [open, property]);

  const selected = properties.find((p) => p._id === propertyId);

  const onPickProperty = (pid) => {
    setPropertyId(pid);
    setVisits(normalizeVisits(properties.find((p) => p._id === pid)));
  };

  const addVisit = () => {
    if (!range || !range[0] || !range[1]) return;
    setVisits((v) => [
      ...v,
      {
        id: `${Date.now()}`,
        startDate: range[0].startOf("day").toISOString(),
        endDate: range[1].endOf("day").toISOString(),
        note: "",
      },
    ]);
    setRange(null);
  };

  const removeVisit = (id) => setVisits((v) => v.filter((x) => x.id !== id));

  return (
    <Modal
      title="Owner visit windows"
      open={open}
      onCancel={onClose}
      okText="Save"
      confirmLoading={busy}
      okButtonProps={{ disabled: !propertyId }}
      onOk={() => onSave(propertyId, visits)}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item label="Property">
          <Select
            showSearch
            value={propertyId}
            placeholder="Select property"
            optionFilterProp="children"
            onChange={onPickProperty}
          >
            {properties.map((p) => (
              <Select.Option key={p._id} value={p._id}>
                {p.propertyName || "Untitled"}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {propertyId && (
          <>
            <div className="sched-visit-list">
              {visits.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No owner visits set"
                />
              ) : (
                visits.map((v) => (
                  <div key={v.id} className="sched-visit-row">
                    <span>
                      <HomeOutlined /> {moment(v.startDate).format("MMM D")} –{" "}
                      {moment(v.endDate || v.startDate).format("MMM D, YYYY")}
                    </span>
                    <Button size="small" danger type="text" onClick={() => removeVisit(v.id)}>
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>
            <Space.Compact style={{ width: "100%" }}>
              <RangePicker
                style={{ width: "100%" }}
                value={range}
                onChange={setRange}
                format="YYYY-MM-DD"
              />
              <Button type="primary" onClick={addVisit} disabled={!range}>
                Add
              </Button>
            </Space.Compact>
            {selected && nextOwnerVisit(selected) && (
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                Next upcoming visit:{" "}
                {moment(nextOwnerVisit(selected).startDate).format("ddd MMM D")}
              </Text>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default InspectionsScheduler;
